// src/components/Hero.jsx
import React from "react";
import { motion } from "framer-motion";
import { FaArrowDown } from "react-icons/fa";
import { Typewriter } from "react-simple-typewriter";
import heroImage from "../assets/hero.jpg"; // fallback image

/**
 * Hero with MP4 video background
 *
 * Props:
 *  - scrollTarget: string (id selector of section to scroll to)
 *  - srcMp4: string (path to MP4, e.g. "/assets/hero.mp4")
 *  - poster: string (optional poster image)
 */
const Hero = ({ scrollTarget, srcMp4 = "/assets/hero.mp4", poster = heroImage }) => {
  const scrollToSection = () => {
    const el = document.querySelector(scrollTarget);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative h-[80vh] md:h-[90vh] flex items-center justify-center text-white overflow-hidden">
      {/* Background video (with fallback poster) */}
      {srcMp4 ? (
        <video
          className="absolute inset-0 h-full w-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster={poster}
        >
          <source src={srcMp4} type="video/mp4" />
          {/* fallback image if video not supported */}
          <img src={poster} alt="Hero background" className="h-full w-full object-cover" />
        </video>
      ) : (
        <img
          src={poster}
          alt="Hero background"
          className="absolute inset-0 h-full w-full object-cover"
        />
      )}

      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/50 z-0"></div>

      {/* Floating blur blobs */}
      <div className="absolute top-10 left-10 w-40 h-40 bg-blue-500 rounded-full mix-blend-multiply blur-3xl opacity-20 animate-pulse z-0"></div>
      <div className="absolute bottom-10 right-10 w-32 h-32 bg-purple-400 rounded-full mix-blend-multiply blur-3xl opacity-20 animate-pulse z-0"></div>

      {/* Hero Content */}
      <motion.div
        className="relative z-10 text-center px-4 max-w-2xl"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
      >
        <motion.h1
          className="text-4xl md:text-5xl font-bold mb-4 leading-tight"
          animate={{ y: [0, -10, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        >
          Your Future in Australia Starts Here
        </motion.h1>

        <p className="text-lg md:text-xl mb-6 text-gray-200 min-h-[60px]">
          <Typewriter
            words={[
              "Expert guidance for studying and settling in Australia.",
              "Get admission, visa, and PR advice — all in one place.",
              "Thousands of students trust Glide for their Australia journey.",
            ]}
            loop
            cursor
            cursorStyle="|"
            typeSpeed={50}
            deleteSpeed={30}
            delaySpeed={2000}
          />
        </p>

        <button
          onClick={scrollToSection}
          aria-label="Scroll to Why Choose Glide section"
          className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-md transition hover:scale-105 hover:shadow-lg duration-300 mx-auto"
        >
          Why Choose Glide <FaArrowDown className="mt-1" />
        </button>
      </motion.div>
    </section>
  );
};

export default Hero;
